/**
 * TriSphere Health & Care — a subscribable care operating system.
 *
 * Bounded contexts wired into one console:
 *   Physical Care · Mental & Emotional Wellbeing · Student Support · Care Operations
 *
 * Event-sourced through `health_events` and RPCs (open_care_episode,
 * administer_medication, close_care_episode, create_health_referral).
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Activity, HeartPulse, ShieldAlert, Stethoscope, Pill, ClipboardList,
  Sparkles, Brain, Users, Package, Wrench, ScanLine, Bell, Siren,
  Sprout, FlaskConical, CheckCircle2, AlertTriangle, Plus, Baby, ArrowRight,
  Search, Droplet, Syringe, User as UserIcon,
} from "lucide-react";
import { shortDateTime, fmtNum } from "@/lib/format";
import { SectionHero, StatTile, Panel, RingGauge, BarRow, LiveDot } from "@/features/superadmin/console-visuals";
import { StudentMedicalRecordModal } from "./student-medical-record-modal";


/* ------------------------------------------------------------------ */
/* Main module                                                         */
/* ------------------------------------------------------------------ */

export function HealthCareModule({ schoolId }: { schoolId: string }) {
  const qc = useQueryClient();

  const { data: dash } = useQuery({
    queryKey: ["health-dashboard", schoolId],
    refetchInterval: 15_000,
    queryFn: async () => (await supabase.rpc("get_health_dashboard", { _school_id: schoolId })).data as any,
  });

  const { data: episodes = [] } = useQuery({
    queryKey: ["care-episodes", schoolId],
    refetchInterval: 12_000,
    queryFn: async () => (await supabase
      .from("care_episodes").select("*, student:students(full_name, class_label, initials, color_hex)")
      .eq("school_id", schoolId).order("opened_at", { ascending: false }).limit(80)).data ?? [],
  });

  const { data: stock = [] } = useQuery({
    queryKey: ["clinic-stock", schoolId],
    queryFn: async () => (await supabase.from("clinic_stock_items").select("*").eq("school_id", schoolId).order("item_name")).data ?? [],
  });

  const { data: equipment = [] } = useQuery({
    queryKey: ["clinic-equipment", schoolId],
    queryFn: async () => (await supabase.from("clinic_equipment").select("*").eq("school_id", schoolId).order("asset_name")).data ?? [],
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["care-plans", schoolId],
    queryFn: async () => (await supabase.from("care_plans")
      .select("*, student:students(full_name, class_label)")
      .eq("school_id", schoolId).eq("status", "active").order("review_date")).data ?? [],
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ["counselling-sessions", schoolId],
    queryFn: async () => (await supabase.from("counselling_sessions")
      .select("*, student:students(full_name, class_label)")
      .eq("school_id", schoolId).order("session_at", { ascending: false }).limit(40)).data ?? [],
  });

  const { data: safeguarding = [] } = useQuery({
    queryKey: ["safeguarding-cases", schoolId],
    queryFn: async () => (await supabase.from("safeguarding_cases")
      .select("*, student:students(full_name, class_label)")
      .eq("school_id", schoolId).order("reported_at", { ascending: false }).limit(40)).data ?? [],
  });

  const { data: sen = [] } = useQuery({
    queryKey: ["sen", schoolId],
    queryFn: async () => (await supabase.from("sen_accommodations")
      .select("*, student:students(full_name, class_label)")
      .eq("school_id", schoolId).eq("active", true).order("created_at", { ascending: false })).data ?? [],
  });

  const { data: campaigns = [] } = useQuery({
    queryKey: ["screening", schoolId],
    queryFn: async () => (await supabase.from("health_screening_campaigns").select("*").eq("school_id", schoolId).order("starts_on", { ascending: false })).data ?? [],
  });

  const { data: alerts = [] } = useQuery({
    queryKey: ["health-alerts", schoolId],
    refetchInterval: 20_000,
    queryFn: async () => (await supabase.from("health_alerts")
      .select("*, student:students(full_name)")
      .eq("school_id", schoolId).order("created_at", { ascending: false }).limit(30)).data ?? [],
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ["care-tasks", schoolId],
    queryFn: async () => (await supabase.from("care_tasks").select("*").eq("school_id", schoolId).eq("status", "pending").order("due_at").limit(30)).data ?? [],
  });

  const seed = useMutation({
    mutationFn: async () => (await supabase.rpc("seed_health_demo", { _school_id: schoolId })).data,
    onSuccess: (res: any) => {
      toast.success(res?.seeded ? `Health demo seeded · ${res.episodes} episodes` : "Health data already exists");
      qc.invalidateQueries({ queryKey: ["health-dashboard"] });
      qc.invalidateQueries({ queryKey: ["care-episodes"] });
      qc.invalidateQueries({ queryKey: ["clinic-stock"] });
      qc.invalidateQueries({ queryKey: ["clinic-equipment"] });
      qc.invalidateQueries({ queryKey: ["care-plans"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const [newEpisodeOpen, setNewEpisodeOpen] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState<any>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);


  const critical = dash?.emergency_active || 0;
  const openCases = dash?.active_cases || 0;
  const stockAlerts = dash?.stock_alerts || 0;

  return (
    <div className="space-y-4">
      <SectionHero
        eyebrow="Care module · v1"
        title="Health & Care"
        subtitle="A connected care operating system — clinical records, care episodes, care plans, mental health, safeguarding, clinic operations and screening."
        icon={HeartPulse}
        tone="terracotta"
        chips={[
          { label: "Active cases", value: String(openCases) },
          { label: "Care plans", value: String(dash?.active_care_plans ?? 0) },
          { label: "Alerts today", value: String(alerts.length) },
          { label: "Stock alerts", value: String(stockAlerts) },
        ]}
        accent={
          <div className="flex flex-col gap-2">
            <Button variant="secondary" onClick={() => setEmergencyOpen(true)} className="gap-1.5">
              <Siren className="h-4 w-4" /> Emergency mode
            </Button>
            <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20" onClick={() => setNewEpisodeOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> New care episode
            </Button>
            <Button variant="ghost" size="sm" className="text-white/90 hover:bg-white/10" onClick={() => seed.mutate()} disabled={seed.isPending}>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Seed demo
            </Button>
          </div>
        }
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <StatTile icon={Stethoscope} label="Today's visits" value={fmtNum(dash?.today_visits)} tone="terracotta" />
        <StatTile icon={Activity} label="Open episodes" value={fmtNum(openCases)} tone="terracotta" delta={openCases > 5 ? { value: "high", positive: false } : undefined} />
        <StatTile icon={ShieldAlert} label="Referrals pending" value={fmtNum(dash?.referrals_pending)} tone="ochre" />
        <StatTile icon={AlertTriangle} label="Allergy flags" value={fmtNum(dash?.allergy_flags)} tone="ochre" />
        <StatTile icon={Pill} label="Chronic conditions" value={fmtNum(dash?.chronic_conditions)} tone="forest" />
        <StatTile icon={Package} label="Stock alerts" value={fmtNum(stockAlerts)} tone="terracotta" />
        <StatTile icon={Wrench} label="Equipment service" value={fmtNum(dash?.equipment_service_due)} tone="ochre" />
        <StatTile icon={ClipboardList} label="Care plans" value={fmtNum(dash?.active_care_plans)} tone="forest" />
        <StatTile icon={Brain} label="Counselling · 7d" value={fmtNum(dash?.counselling_this_week)} tone="ink" />
        <StatTile icon={Users} label="Safeguarding open" value={fmtNum(dash?.open_safeguarding)} tone="terracotta" />
        <StatTile icon={CheckCircle2} label="Tasks due today" value={fmtNum(dash?.tasks_due_today)} tone="forest" />
        <StatTile icon={Siren} label="Emergency active" value={fmtNum(critical)} tone="terracotta" delta={critical ? { value: "LIVE", positive: false } : undefined} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
          <TabsTrigger value="physical">Physical care</TabsTrigger>

          <TabsTrigger value="plans">Care plans</TabsTrigger>
          <TabsTrigger value="wellbeing">Wellbeing</TabsTrigger>
          <TabsTrigger value="safeguarding">Safeguarding</TabsTrigger>
          <TabsTrigger value="sen">SEN</TabsTrigger>
          <TabsTrigger value="operations">Clinic ops</TabsTrigger>
          <TabsTrigger value="screening">Screening</TabsTrigger>
          <TabsTrigger value="alerts">Alerts & events</TabsTrigger>
        </TabsList>

        {/* ================= OVERVIEW ================= */}
        <TabsContent value="overview" className="mt-4 grid gap-3 lg:grid-cols-3">
          <Panel title="Live queue" icon={Activity} badge={<Badge variant="outline" className="ml-2">{episodes.filter((e:any)=>e.status==='open').length} open</Badge>} className="lg:col-span-2">
            <div className="space-y-2 max-h-[400px] overflow-auto">
              {episodes.filter((e:any)=>e.status==='open').slice(0, 12).map((e:any) => (
                <EpisodeRow key={e.id} episode={e} onOpen={() => setSelectedEpisode(e)} />
              ))}
              {episodes.filter((e:any)=>e.status==='open').length === 0 && (
                <EmptyState icon={CheckCircle2} label="No active episodes · clinic clear" />
              )}
            </div>
          </Panel>

          <Panel title="Care tasks · today" icon={ClipboardList}>
            <div className="space-y-2 max-h-[400px] overflow-auto">
              {tasks.slice(0, 12).map((t:any) => (
                <div key={t.id} className="flex items-start justify-between rounded-lg border p-2.5 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{t.title}</div>
                    <div className="text-xs text-muted-foreground">{t.task_type} · {t.due_at ? shortDateTime(t.due_at) : 'no due'}</div>
                  </div>
                  <Badge variant={t.priority === 'urgent' ? 'destructive' : 'outline'} className="text-[10px]">{t.priority}</Badge>
                </div>
              ))}
              {tasks.length === 0 && <EmptyState icon={CheckCircle2} label="No pending tasks" />}
            </div>
          </Panel>

          <Panel title="Care plan readiness" icon={Sprout}>
            <div className="flex items-center justify-around">
              <RingGauge value={Math.min(100, ((dash?.active_care_plans ?? 0) / Math.max(1, (dash?.chronic_conditions ?? 1))) * 100)} label="coverage" tone="forest" />
              <div className="text-xs text-muted-foreground space-y-1">
                <div>{fmtNum(dash?.active_care_plans)} active plans</div>
                <div>{fmtNum(dash?.chronic_conditions)} chronic conditions</div>
                <div>Reviews due · {plans.filter((p:any)=>p.review_date && new Date(p.review_date) < new Date(Date.now() + 14*86400000)).length}</div>
              </div>
            </div>
          </Panel>

          <Panel title="Equipment readiness" icon={Wrench}>
            <div className="space-y-2 text-sm">
              {['ok','service_due','broken'].map(st => (
                <BarRow key={st} label={st.replace('_',' ')}
                  value={equipment.filter((e:any)=>e.status===st).length}
                  max={equipment.length || 1}
                  tone={st==='ok'?'forest':st==='service_due'?'ochre':'terracotta'}
                  right={<span>{equipment.filter((e:any)=>e.status===st).length}</span>}
                />
              ))}
            </div>
          </Panel>

          <Panel title="Stock health" icon={Package}>
            <div className="space-y-2 text-sm">
              {stock.slice(0, 6).map((s:any) => (
                <BarRow key={s.id} label={s.item_name} value={s.quantity} max={Math.max(s.reorder_level * 3, s.quantity)}
                  tone={s.quantity <= s.reorder_level ? 'terracotta' : s.quantity <= s.reorder_level * 2 ? 'ochre' : 'forest'}
                  right={<span>{s.quantity} {s.unit_of_measure}</span>} />
              ))}
            </div>
          </Panel>
        </TabsContent>

        {/* ================= STUDENTS ================= */}
        <TabsContent value="students" className="mt-4">
          <StudentsHealthRoster schoolId={schoolId} onOpen={setSelectedStudentId} />
        </TabsContent>


        <TabsContent value="physical" className="mt-4 space-y-3">
          <Panel title="All care episodes" icon={Stethoscope} right={<Button size="sm" onClick={() => setNewEpisodeOpen(true)}><Plus className="mr-1 h-3.5 w-3.5" /> New episode</Button>}>
            <div className="space-y-2 max-h-[500px] overflow-auto">
              {episodes.map((e:any) => (
                <EpisodeRow key={e.id} episode={e} onOpen={() => setSelectedEpisode(e)} />
              ))}
              {episodes.length === 0 && <EmptyState icon={Stethoscope} label="No care episodes yet · seed demo to try the workflow" />}
            </div>
          </Panel>
        </TabsContent>

        {/* ================= CARE PLANS ================= */}
        <TabsContent value="plans" className="mt-4">
          <Panel title="Chronic care plans" icon={Sprout} subtitle="Asthma · Diabetes · Epilepsy · Sickle cell · Severe allergy · ADHD">
            <div className="grid gap-3 md:grid-cols-2">
              {plans.map((p:any) => (
                <Card key={p.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold">{p.plan_name}</div>
                      <div className="text-xs text-muted-foreground">{p.student?.full_name} · {p.student?.class_label}</div>
                    </div>
                    <Badge variant="outline">Review {p.review_date ? new Date(p.review_date).toLocaleDateString() : '—'}</Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <JsonList title="Triggers" items={p.triggers} />
                    <JsonList title="Monitoring" items={p.monitoring} />
                    <JsonList title="Medications" items={(p.medications||[]).map((m:any)=>`${m.name} ${m.dose||''}`)} />
                    <JsonList title="Escalation" items={p.escalation} />
                  </div>
                </Card>
              ))}
              {plans.length === 0 && <EmptyState icon={Sprout} label="No active care plans" />}
            </div>
          </Panel>
        </TabsContent>

        {/* ================= WELLBEING ================= */}
        <TabsContent value="wellbeing" className="mt-4 grid gap-3 lg:grid-cols-2">
          <Panel title="Counselling sessions" icon={Brain} subtitle="Confidential · counsellor-scoped">
            <div className="space-y-2 max-h-[420px] overflow-auto">
              {sessions.map((s:any) => (
                <div key={s.id} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="font-medium">{s.student?.full_name}</div>
                    <Badge variant="outline">{s.session_type}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{shortDateTime(s.session_at)} · {s.duration_min} min · {s.topic || '—'}</div>
                  {s.outcome && <div className="mt-1 text-xs">{s.outcome}</div>}
                </div>
              ))}
              {sessions.length === 0 && <EmptyState icon={Brain} label="No counselling sessions" />}
            </div>
          </Panel>
          <Panel title="Mental health · trend" icon={HeartPulse}>
            <MentalHealthTrend schoolId={schoolId} />
          </Panel>
        </TabsContent>

        {/* ================= SAFEGUARDING ================= */}
        <TabsContent value="safeguarding" className="mt-4">
          <Panel title="Safeguarding cases" icon={ShieldAlert} subtitle="Confidential · restricted access">
            <div className="space-y-2">
              {safeguarding.map((c:any) => (
                <div key={c.id} className="rounded-lg border p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm font-semibold">{c.student?.full_name}</div>
                      <div className="text-xs text-muted-foreground">{c.case_type} · {shortDateTime(c.reported_at)}</div>
                    </div>
                    <div className="flex gap-1">
                      <Badge variant={c.severity==='high'?'destructive':'outline'}>{c.severity}</Badge>
                      <Badge variant={c.status==='open'?'destructive':'secondary'}>{c.status}</Badge>
                    </div>
                  </div>
                  {c.narrative && <div className="mt-2 text-xs text-muted-foreground line-clamp-2">{c.narrative}</div>}
                </div>
              ))}
              {safeguarding.length === 0 && <EmptyState icon={ShieldAlert} label="No safeguarding cases · good news" />}
            </div>
          </Panel>
        </TabsContent>

        {/* ================= SEN ================= */}
        <TabsContent value="sen" className="mt-4">
          <Panel title="SEN & accommodations" icon={Baby} subtitle="Learning · Physical · Sensory · Behavioural · Speech">
            <div className="grid gap-2 md:grid-cols-2">
              {sen.map((s:any) => (
                <Card key={s.id} className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="text-sm">
                      <div className="font-semibold">{s.student?.full_name}</div>
                      <div className="text-xs text-muted-foreground">{s.student?.class_label} · {s.category}</div>
                    </div>
                    <Badge variant="outline">{s.category}</Badge>
                  </div>
                  {s.description && <div className="mt-1 text-xs">{s.description}</div>}
                </Card>
              ))}
              {sen.length === 0 && <EmptyState icon={Baby} label="No SEN records yet" />}
            </div>
          </Panel>
        </TabsContent>

        {/* ================= CLINIC OPS ================= */}
        <TabsContent value="operations" className="mt-4 grid gap-3 lg:grid-cols-2">
          <Panel title="Stock & medicines" icon={Package}>
            <div className="space-y-2 max-h-[500px] overflow-auto">
              {stock.map((s:any) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg border p-2.5 text-sm">
                  <div>
                    <div className="font-medium">{s.item_name} {s.controlled && <Badge variant="destructive" className="ml-1 text-[9px]">controlled</Badge>}</div>
                    <div className="text-xs text-muted-foreground">{s.category} · reorder at {s.reorder_level}</div>
                  </div>
                  <div className={`text-right ${s.quantity <= s.reorder_level ? 'text-[var(--terracotta)]' : 'text-foreground'}`}>
                    <div className="font-display text-lg leading-none">{s.quantity}</div>
                    <div className="text-[10px] uppercase">{s.unit_of_measure}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Equipment & maintenance" icon={Wrench}>
            <div className="space-y-2 max-h-[500px] overflow-auto">
              {equipment.map((e:any) => (
                <div key={e.id} className="flex items-center justify-between rounded-lg border p-2.5 text-sm">
                  <div>
                    <div className="font-medium">{e.asset_name}</div>
                    <div className="text-xs text-muted-foreground">{e.asset_type} · {e.serial_no || '—'}</div>
                    {e.service_due_at && <div className="text-[10px] text-muted-foreground">Service due {new Date(e.service_due_at).toLocaleDateString()}</div>}
                  </div>
                  <Badge variant={e.status==='ok'?'secondary':e.status==='service_due'?'outline':'destructive'}>{e.status.replace('_',' ')}</Badge>
                </div>
              ))}
            </div>
          </Panel>
        </TabsContent>

        {/* ================= SCREENING ================= */}
        <TabsContent value="screening" className="mt-4">
          <Panel title="Screening campaigns" icon={ScanLine} subtitle="Vision · Dental · Hearing · BMI · Deworming · Vaccination">
            <div className="grid gap-3 md:grid-cols-3">
              {campaigns.map((c:any) => (
                <Card key={c.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="font-semibold">{c.campaign_name}</div>
                    <Badge variant={c.status==='in_progress'?'destructive':c.status==='completed'?'secondary':'outline'}>{c.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {c.starts_on && new Date(c.starts_on).toLocaleDateString()} – {c.ends_on && new Date(c.ends_on).toLocaleDateString()}
                  </div>
                </Card>
              ))}
              {campaigns.length === 0 && <EmptyState icon={ScanLine} label="No screening campaigns scheduled" />}
            </div>
          </Panel>
        </TabsContent>

        {/* ================= ALERTS & EVENTS ================= */}
        <TabsContent value="alerts" className="mt-4 grid gap-3 lg:grid-cols-2">
          <Panel title="Health alerts" icon={Bell} badge={<LiveDot tone="terracotta" />}>
            <div className="space-y-2 max-h-[500px] overflow-auto">
              {alerts.map((a:any) => (
                <div key={a.id} className="rounded-lg border p-2.5 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{a.student?.full_name || 'Student'}</div>
                    <Badge variant="outline" className="text-[10px]">{a.alert_type}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{a.message}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{shortDateTime(a.created_at)} → {a.recipient_type}</div>
                </div>
              ))}
              {alerts.length === 0 && <EmptyState icon={Bell} label="Alert stream quiet" />}
            </div>
          </Panel>
          <Panel title="Event log" icon={FlaskConical} subtitle="Immutable · event-sourced">
            <div className="space-y-1.5 max-h-[500px] overflow-auto font-mono text-[11px]">
              {(dash?.recent_events || []).map((ev: any, i: number) => (
                <div key={i} className="flex justify-between border-l-2 border-[var(--forest)]/40 pl-2">
                  <span className="text-[var(--forest)]">{ev.event_type}</span>
                  <span className="text-muted-foreground">{shortDateTime(ev.created_at)}</span>
                </div>
              ))}
            </div>
          </Panel>
        </TabsContent>
      </Tabs>

      {newEpisodeOpen && <NewEpisodeDialog schoolId={schoolId} onClose={() => setNewEpisodeOpen(false)} />}
      {emergencyOpen && <EmergencyDialog schoolId={schoolId} onClose={() => setEmergencyOpen(false)} />}
      {selectedEpisode && <EpisodeDrawer episode={selectedEpisode} onClose={() => setSelectedEpisode(null)} />}
      {selectedStudentId && <StudentMedicalRecordModal studentId={selectedStudentId} onClose={() => setSelectedStudentId(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Students health roster                                              */
/* ------------------------------------------------------------------ */

function StudentsHealthRoster({ schoolId, onOpen }: { schoolId: string; onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "allergy" | "chronic" | "open" | "meds">("all");

  const { data: students = [] } = useQuery({
    queryKey: ["health-roster-students", schoolId],
    queryFn: async () => (await supabase.from("students")
      .select("id, full_name, initials, color_hex, class_label, student_no, boarding")
      .eq("school_id", schoolId).order("full_name").limit(500)).data ?? [],
  });

  const { data: allergyMap = {} } = useQuery({
    queryKey: ["health-roster-allergies", schoolId],
    queryFn: async () => {
      const ids = (students as any[]).map(s => s.id);
      if (!ids.length) return {};
      const r = await supabase.from("health_allergies").select("student_id, allergen, severity").in("student_id", ids).eq("active", true);
      const m: Record<string, any[]> = {};
      (r.data ?? []).forEach((a: any) => { (m[a.student_id] ||= []).push(a); });
      return m;
    },
    enabled: students.length > 0,
  });

  const { data: conditionMap = {} } = useQuery({
    queryKey: ["health-roster-conditions", schoolId],
    queryFn: async () => {
      const ids = (students as any[]).map(s => s.id);
      if (!ids.length) return {};
      const r = await supabase.from("health_conditions").select("student_id, condition_name").in("student_id", ids).eq("active", true);
      const m: Record<string, any[]> = {};
      (r.data ?? []).forEach((a: any) => { (m[a.student_id] ||= []).push(a); });
      return m;
    },
    enabled: students.length > 0,
  });

  const { data: episodeMap = {} } = useQuery({
    queryKey: ["health-roster-episodes", schoolId],
    queryFn: async () => {
      const r = await supabase.from("care_episodes").select("student_id, status, severity, opened_at").eq("school_id", schoolId).order("opened_at", { ascending: false });
      const m: Record<string, any[]> = {};
      (r.data ?? []).forEach((a: any) => { (m[a.student_id] ||= []).push(a); });
      return m;
    },
  });

  const { data: medsMap = {} } = useQuery({
    queryKey: ["health-roster-meds", schoolId],
    queryFn: async () => {
      const ids = (students as any[]).map(s => s.id);
      if (!ids.length) return {};
      const r = await supabase.from("health_medications").select("student_id, medicine_name, ongoing").in("student_id", ids).eq("ongoing", true);
      const m: Record<string, any[]> = {};
      (r.data ?? []).forEach((a: any) => { (m[a.student_id] ||= []).push(a); });
      return m;
    },
    enabled: students.length > 0,
  });

  const { data: bgMap = {} } = useQuery({
    queryKey: ["health-roster-bg", schoolId],
    queryFn: async () => {
      const ids = (students as any[]).map(s => s.id);
      if (!ids.length) return {};
      const r = await supabase.from("health_records").select("student_id, blood_group").in("student_id", ids);
      const m: Record<string, string> = {};
      (r.data ?? []).forEach((a: any) => { m[a.student_id] = a.blood_group; });
      return m;
    },
    enabled: students.length > 0,
  });

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (students as any[]).filter(s => {
      if (term && !(s.full_name?.toLowerCase().includes(term) || s.student_no?.toLowerCase().includes(term) || s.class_label?.toLowerCase().includes(term))) return false;
      const a = (allergyMap as any)[s.id] || [];
      const c = (conditionMap as any)[s.id] || [];
      const e = ((episodeMap as any)[s.id] || []).filter((x: any) => x.status === "open");
      const m = (medsMap as any)[s.id] || [];
      if (filter === "allergy" && !a.length) return false;
      if (filter === "chronic" && !c.length) return false;
      if (filter === "open" && !e.length) return false;
      if (filter === "meds" && !m.length) return false;
      return true;
    });
  }, [students, q, filter, allergyMap, conditionMap, episodeMap, medsMap]);

  const counts = {
    all: students.length,
    allergy: Object.keys(allergyMap).length,
    chronic: Object.keys(conditionMap).length,
    open: Object.values(episodeMap).filter((v: any) => v.some((x: any) => x.status === "open")).length,
    meds: Object.keys(medsMap).length,
  };

  return (
    <Panel title="Student health roster" icon={Users} subtitle="Every learner · one record · one tap to the passport"
      right={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or class" className="h-8 w-56 pl-7 text-xs" />
          </div>
        </div>
      }>

      {/* Filter chips */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {([
          { k: "all", label: "All", i: Users, tone: "bg-muted" },
          { k: "allergy", label: "Allergies", i: AlertTriangle, tone: "bg-[var(--terracotta,#A42022)]/10 text-[var(--terracotta,#A42022)]" },
          { k: "chronic", label: "Chronic", i: Sparkles, tone: "bg-[var(--forest,#0d7a5f)]/10 text-[var(--forest,#0d7a5f)]" },
          { k: "open", label: "Open episodes", i: Stethoscope, tone: "bg-[var(--ochre,#c9a84c)]/15 text-[#8a6d20]" },
          { k: "meds", label: "On medication", i: Pill, tone: "bg-blue-500/10 text-blue-700" },
        ] as const).map(c => (
          <button key={c.k} onClick={() => setFilter(c.k)}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 transition
              ${filter === c.k ? "ring-foreground/40" : "ring-transparent hover:ring-border"} ${c.tone}`}>
            <c.i className="h-3 w-3" /> {c.label}
            <span className="ml-0.5 rounded bg-background/60 px-1 text-[9px]">{(counts as any)[c.k]}</span>
          </button>
        ))}
      </div>

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3 max-h-[640px] overflow-auto pr-1">
        {rows.map((s: any) => {
          const a = (allergyMap as any)[s.id] || [];
          const c = (conditionMap as any)[s.id] || [];
          const e = ((episodeMap as any)[s.id] || []).filter((x: any) => x.status === "open");
          const m = (medsMap as any)[s.id] || [];
          const bg = (bgMap as any)[s.id];
          const risk = a.some((x: any) => x.severity === "severe") ? "high" : a.length || c.length || e.length ? "watch" : "clear";
          return (
            <button key={s.id} onClick={() => onOpen(s.id)}
              className="group relative overflow-hidden rounded-xl border bg-card p-3 text-left transition hover:shadow-lg hover:-translate-y-0.5">
              <div className="absolute inset-x-0 top-0 h-1"
                style={{ background: risk === "high" ? "linear-gradient(90deg, #A42022, #c9a84c)"
                       : risk === "watch" ? "linear-gradient(90deg, #c9a84c, transparent)"
                       : "linear-gradient(90deg, #0d7a5f, transparent)" }} />
              <div className="flex items-start gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white font-semibold text-sm shadow"
                  style={{ background: `linear-gradient(135deg, ${s.color_hex ?? "#0d7a5f"}, #0f172a)` }}>
                  {s.initials || <UserIcon className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-sm">{s.full_name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{s.class_label} · {s.student_no}</div>
                    </div>
                    {bg && (
                      <span className="inline-flex items-center gap-0.5 rounded-md bg-[var(--terracotta,#A42022)]/10 px-1.5 py-0.5 text-[10px] font-bold text-[var(--terracotta,#A42022)]">
                        <Droplet className="h-2.5 w-2.5" /> {bg}
                      </span>
                    )}
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1">
                    {a.slice(0, 2).map((x: any, i: number) => (
                      <span key={i} className="inline-flex items-center gap-0.5 rounded-full bg-[var(--terracotta,#A42022)]/10 px-1.5 py-0.5 text-[9px] font-medium text-[var(--terracotta,#A42022)]">
                        <AlertTriangle className="h-2.5 w-2.5" />{x.allergen}
                      </span>
                    ))}
                    {a.length > 2 && <span className="text-[9px] text-muted-foreground">+{a.length - 2}</span>}
                    {c.slice(0, 2).map((x: any, i: number) => (
                      <span key={i} className="inline-flex items-center gap-0.5 rounded-full bg-[var(--forest,#0d7a5f)]/10 px-1.5 py-0.5 text-[9px] font-medium text-[var(--forest,#0d7a5f)]">
                        <Sparkles className="h-2.5 w-2.5" />{x.condition_name}
                      </span>
                    ))}
                    {m.length > 0 && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-medium text-blue-700">
                        <Pill className="h-2.5 w-2.5" />{m.length} med{m.length > 1 ? "s" : ""}
                      </span>
                    )}
                    {e.length > 0 && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--ochre,#c9a84c)]/20 px-1.5 py-0.5 text-[9px] font-semibold text-[#8a6d20]">
                        <Stethoscope className="h-2.5 w-2.5" />{e.length} open
                      </span>
                    )}
                    {!a.length && !c.length && !m.length && !e.length && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700">
                        <CheckCircle2 className="h-2.5 w-2.5" /> clear
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ring-1
                  ${risk === "high" ? "ring-[var(--terracotta,#A42022)]/40 text-[var(--terracotta,#A42022)]"
                    : risk === "watch" ? "ring-[var(--ochre,#c9a84c)]/50 text-[#8a6d20]"
                    : "ring-emerald-500/30 text-emerald-700"}`}>
                  <HeartPulse className="h-2.5 w-2.5" /> {risk === "high" ? "High priority" : risk === "watch" ? "Watchlist" : "Clear"}
                </span>
                <span className="opacity-0 group-hover:opacity-100 transition">Open passport →</span>
              </div>
            </button>
          );
        })}
        {rows.length === 0 && <div className="col-span-full"><EmptyState icon={Users} label="No students match this filter" /></div>}
      </div>
    </Panel>
  );
}


/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function EpisodeRow({ episode, onOpen }: { episode: any; onOpen: () => void }) {
  const sev = episode.severity;
  const sevColor = sev === 'critical' ? 'destructive' : sev === 'high' ? 'destructive' : sev === 'moderate' ? 'outline' : 'secondary';
  return (
    <button onClick={onOpen} className="w-full text-left rounded-lg border p-2.5 hover:bg-accent transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="grid h-8 w-8 place-items-center rounded-full text-white text-xs font-semibold" style={{ background: episode.student?.color_hex || '#A42022' }}>
            {episode.student?.initials || '·'}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{episode.student?.full_name || 'Unknown'}</div>
            <div className="text-[11px] text-muted-foreground">{episode.reason || '—'} · {episode.triage_status}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant={sevColor as any} className="text-[10px]">{sev}</Badge>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
      </div>
    </button>
  );
}

function JsonList({ title, items }: { title: string; items?: string[] }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{title}</div>
      <ul className="mt-0.5 space-y-0.5">
        {(items || []).slice(0, 4).map((it, i) => <li key={i} className="truncate">· {it}</li>)}
        {!items?.length && <li className="text-muted-foreground">—</li>}
      </ul>
    </div>
  );
}

function EmptyState({ icon: Icon, label }: { icon: any; label: string }) {
  return (
    <div className="grid place-items-center py-8 text-muted-foreground text-sm">
      <Icon className="h-8 w-8 opacity-30 mb-2" />
      {label}
    </div>
  );
}

function MentalHealthTrend({ schoolId }: { schoolId: string }) {
  const { data = [] } = useQuery({
    queryKey: ["mh-trend", schoolId],
    queryFn: async () => (await supabase.from("mental_health_checkins")
      .select("mood_score, submitted_at, student:students!inner(school_id)")
      .eq("student.school_id", schoolId)
      .order("submitted_at", { ascending: false }).limit(60)).data ?? [],
  });
  const avg = data.length ? (data.reduce((a: number, r: any) => a + (r.mood_score || 0), 0) / data.length) : 0;
  return (
    <div className="flex items-center justify-around">
      <RingGauge value={avg * 10} label="avg mood" tone="ink" />
      <div className="text-xs text-muted-foreground">
        <div>{data.length} check-ins captured</div>
        <div>Flagged: {data.filter((d: any) => d.mood_score && d.mood_score < 5).length}</div>
        <div className="mt-2 text-[10px]">Parents & counsellors can act on flagged scores.</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dialogs                                                             */
/* ------------------------------------------------------------------ */

function NewEpisodeDialog({ schoolId, onClose }: { schoolId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [studentId, setStudentId] = useState<string>("");
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState("mild");
  const [symptoms, setSymptoms] = useState<string[]>([]);

  const { data: students = [] } = useQuery({
    queryKey: ["students-for-episode", schoolId],
    queryFn: async () => (await supabase.from("students").select("id, full_name, class_label").eq("school_id", schoolId).order("full_name").limit(200)).data ?? [],
  });

  const mut = useMutation({
    mutationFn: async () => {
      const r = await supabase.rpc("open_care_episode", {
        _student_id: studentId, _reason: reason,
        _symptoms: symptoms as any, _severity: severity,
      });
      if (r.error) throw r.error;
      return r.data;
    },
    onSuccess: () => {
      toast.success("Care episode opened");
      qc.invalidateQueries({ queryKey: ["care-episodes"] });
      qc.invalidateQueries({ queryKey: ["health-dashboard"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const symptomOptions = ["Fever", "Headache", "Cough", "Stomach ache", "Injury", "Rash", "Vomiting", "Dizziness"];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Stethoscope className="h-4 w-4" /> New care episode</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Student</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent>{students.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.class_label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. persistent fever" />
          </div>
          <div>
            <Label>Symptoms</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {symptomOptions.map(s => {
                const on = symptoms.includes(s);
                return <Button key={s} type="button" size="sm" variant={on ? "default" : "outline"}
                  onClick={() => setSymptoms(x => on ? x.filter(y => y !== s) : [...x, s])}>{s}</Button>;
              })}
            </div>
          </div>
          <div>
            <Label>Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mild">Mild</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!studentId || !reason || mut.isPending} onClick={() => mut.mutate()}>Open episode</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmergencyDialog({ schoolId, onClose }: { schoolId: string; onClose: () => void }) {
  const [studentId, setStudentId] = useState("");
  const [type, setType] = useState("medical");
  const [location, setLocation] = useState("");
  const qc = useQueryClient();

  const { data: students = [] } = useQuery({
    queryKey: ["students-for-emergency", schoolId],
    queryFn: async () => (await supabase.from("students").select("id, full_name, class_label").eq("school_id", schoolId).order("full_name").limit(200)).data ?? [],
  });

  const mut = useMutation({
    mutationFn: async () => {
      const r = await supabase.from("emergency_incidents").insert({
        school_id: schoolId, student_id: studentId || null, incident_type: type,
        severity: 'critical', location, status: 'active',
      });
      if (r.error) throw r.error;
    },
    onSuccess: () => { toast.error("Emergency declared · responders notified"); qc.invalidateQueries({ queryKey: ["health-dashboard"] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--terracotta)]"><Siren className="h-5 w-5" /> Declare emergency</DialogTitle>
        </DialogHeader>
        <motion.div initial={{ scale: 0.98 }} animate={{ scale: 1 }} className="rounded-xl border-2 border-[var(--terracotta)] bg-[var(--terracotta)]/5 p-3 text-sm">
          Emergency mode notifies responders, opens an incident timeline, and starts a stopwatch. Under 5 seconds to declare.
        </motion.div>
        <div className="space-y-3">
          <div><Label>Student (optional)</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger><SelectValue placeholder="Unattributed" /></SelectTrigger>
              <SelectContent>{students.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="medical">Medical</SelectItem>
                <SelectItem value="anaphylaxis">Anaphylaxis</SelectItem>
                <SelectItem value="seizure">Seizure</SelectItem>
                <SelectItem value="injury">Injury</SelectItem>
                <SelectItem value="safeguarding">Safeguarding</SelectItem>
                <SelectItem value="evacuation">Evacuation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Location</Label><Input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Assembly hall" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={mut.isPending} onClick={() => mut.mutate()}>
            <Siren className="mr-1.5 h-4 w-4" /> Declare
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EpisodeDrawer({ episode, onClose }: { episode: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [medicine, setMedicine] = useState("");
  const [dose, setDose] = useState("");
  const [refReason, setRefReason] = useState("");
  const [refType, setRefType] = useState("parent_pickup");

  const { data: obs = [] } = useQuery({
    queryKey: ["episode-obs", episode.id],
    queryFn: async () => (await supabase.from("care_observations").select("*").eq("episode_id", episode.id).order("recorded_at", { ascending: false })).data ?? [],
  });
  const { data: meds = [] } = useQuery({
    queryKey: ["episode-meds", episode.id],
    queryFn: async () => (await supabase.from("medication_administrations").select("*").eq("episode_id", episode.id).order("administered_at", { ascending: false })).data ?? [],
  });
  const { data: allergies = [] } = useQuery({
    queryKey: ["episode-allergies", episode.student_id],
    queryFn: async () => (await supabase.from("health_allergies").select("*").eq("student_id", episode.student_id).eq("active", true)).data ?? [],
  });

  const admin = useMutation({
    mutationFn: async () => {
      const r = await supabase.rpc("administer_medication", {
        _episode_id: episode.id, _student_id: episode.student_id,
        _medicine: medicine, _dose: dose, _route: 'oral', _override_allergy: false,
      });
      if (r.error) throw r.error;
    },
    onSuccess: () => { toast.success("Medication logged"); setMedicine(""); setDose("");
      qc.invalidateQueries({ queryKey: ["episode-meds", episode.id] }); },
    onError: (e: any) => toast.error(e.message),
  });

  const refer = useMutation({
    mutationFn: async () => {
      const r = await supabase.rpc("create_health_referral", {
        _episode_id: episode.id, _referral_type: refType,
        _destination: refType === 'hospital' ? 'Nearest hospital' : 'Home',
        _reason: refReason, _urgency: 'high', _needs_pickup: true,
      });
      if (r.error) throw r.error;
    },
    onSuccess: () => { toast.success("Referral created · parent notified"); setRefReason("");
      qc.invalidateQueries({ queryKey: ["care-episodes"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  const close = useMutation({
    mutationFn: async () => {
      const r = await supabase.rpc("close_care_episode", {
        _episode_id: episode.id, _outcome: 'returned_to_class',
        _clearance: 'Fit to return', _follow_up_at: undefined,
      });
      if (r.error) throw r.error;
    },
    onSuccess: () => { toast.success("Episode closed · parent notified"); qc.invalidateQueries({ queryKey: ["care-episodes"] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4" />
            {episode.student?.full_name || 'Episode'} · {episode.severity}
          </DialogTitle>
        </DialogHeader>

        {allergies.length > 0 && (
          <div className="rounded-lg border-2 border-[var(--terracotta)] bg-[var(--terracotta)]/5 p-2.5 text-sm">
            <div className="flex items-center gap-1.5 font-semibold text-[var(--terracotta)]">
              <AlertTriangle className="h-4 w-4" /> Allergy alerts
            </div>
            <div className="mt-1 text-xs">
              {allergies.map((a: any) => <div key={a.id}>· {a.allergen} — {a.reaction} ({a.severity})</div>)}
            </div>
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <Card className="p-3">
            <div className="text-xs font-semibold uppercase text-muted-foreground">Observations</div>
            <div className="mt-2 space-y-1 text-sm max-h-40 overflow-auto">
              {obs.map((o: any) => (
                <div key={o.id} className="flex justify-between border-b pb-1">
                  <span>{o.observation_type}</span>
                  <span className="font-medium">{o.value_numeric} {o.unit}</span>
                </div>
              ))}
              {obs.length === 0 && <div className="text-muted-foreground text-xs">No observations yet</div>}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-xs font-semibold uppercase text-muted-foreground">Medications given</div>
            <div className="mt-2 space-y-1 text-sm max-h-40 overflow-auto">
              {meds.map((m: any) => (
                <div key={m.id} className="border-b pb-1">
                  <div className="font-medium">{m.medicine_name} {m.dose_given}</div>
                  <div className="text-[10px] text-muted-foreground">{shortDateTime(m.administered_at)}</div>
                </div>
              ))}
              {meds.length === 0 && <div className="text-muted-foreground text-xs">None yet</div>}
            </div>
          </Card>
        </div>

        <Card className="p-3 space-y-2">
          <div className="text-xs font-semibold uppercase">Administer medication</div>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
            <Input placeholder="Medicine (e.g. Paracetamol)" value={medicine} onChange={e => setMedicine(e.target.value)} />
            <Input placeholder="Dose" value={dose} onChange={e => setDose(e.target.value)} className="w-32" />
            <Button disabled={!medicine || !dose || admin.isPending} onClick={() => admin.mutate()}><Pill className="mr-1 h-3.5 w-3.5" /> Administer</Button>
          </div>
        </Card>

        <Card className="p-3 space-y-2">
          <div className="text-xs font-semibold uppercase">Refer / Escalate</div>
          <div className="grid gap-2 sm:grid-cols-[auto_1fr_auto]">
            <Select value={refType} onValueChange={setRefType}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parent_pickup">Parent pickup</SelectItem>
                <SelectItem value="hospital">Hospital</SelectItem>
                <SelectItem value="clinic">Clinic</SelectItem>
                <SelectItem value="specialist">Specialist</SelectItem>
                <SelectItem value="observation">Observation</SelectItem>
              </SelectContent>
            </Select>
            <Textarea rows={2} placeholder="Reason" value={refReason} onChange={e => setRefReason(e.target.value)} />
            <Button disabled={!refReason || refer.isPending} variant="secondary" onClick={() => refer.mutate()}><Siren className="mr-1 h-3.5 w-3.5" /> Refer</Button>
          </div>
        </Card>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {episode.status === 'open' && <Button onClick={() => close.mutate()} disabled={close.isPending}><CheckCircle2 className="mr-1 h-4 w-4" /> Close & clear</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
